import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
    useAdjustStockByBatchesMutation,
    useLazyGetBatchesByProductVariationIdQuery,
} from '@/features/slice/inventory';
import { toastError } from '@/utils';

import { Route } from '../../../../constants';
import { CreateStockAdjustment } from './schema';

export const useCreateStockAdjustment = () => {
    const [selectedProductId, setSelectedProductId] = useState<string | null>(
        null,
    );
    const [selectedProductName, setSelectedProductName] = useState<
        string | null
    >(null);

    const [
        getBatchesByProductVariationId,
        {
            data: batchesData,
            isLoading: isBatchesLoading,
            isError: isBatchesError,
            isFetching: isBatchesFetching,
            error: batchesError,
        },
    ] = useLazyGetBatchesByProductVariationIdQuery();

    const [adjustStockByBatches, { isLoading: isAdjustUpdateLoading }] =
        useAdjustStockByBatchesMutation();

    const handleSelectedProductChange = async (
        productVariationId: string,
        name: string,
    ) => {
        if (!productVariationId || !name) {
            setSelectedProductId(null);
            setSelectedProductName(null);
            return;
        }
        setSelectedProductId(productVariationId);
        setSelectedProductName(name);
        await getBatchesByProductVariationId(productVariationId);
    };

    const navigate = useNavigate();

    const handleSubmitAdjustment = async (payload: CreateStockAdjustment) => {
        const requestBatches =
            payload?.items
                ?.map((item) => ({
                    purchase_item_id: item.purchaseItemId,
                    product_variation_id: item.productVariationId,
                    quantity: item.quantity,
                    note: payload.note,
                }))
                .filter((item) => item.quantity != 0) ?? [];
        try {
            await adjustStockByBatches({
                batches: requestBatches,
            }).unwrap();
            toast.success('Adjust successfully adjusted');
            navigate(Route.ADJUSTMENT_STOCKS);
        } catch (error) {
            toastError(
                error,
                'useCreateStockAdjustment/handleSubmitAdjustment',
            );
        }
    };

    return {
        selectedProductId,
        setSelectedProductId,
        handleSelectedProductChange,
        selectedProductName,
        setSelectedProductName,

        getBatchesByProductVariationId,
        batchesData,
        isBatchesLoading,
        isBatchesError,
        isBatchesFetching,
        batchesError,

        adjustStockByBatches,
        isAdjustUpdateLoading,

        handleSubmitAdjustment,
    };
};
